import { api, type HistoryItem } from '../api';
import { requireAuthOrRedirect } from '../auth';
import { renderNav } from '../nav';
import { confirmDialog, coverEl, fmtDateGroup, fmtTime, toast, pluginContentTypes, workHref } from '../utils';
import { getCachedSlug, warmSlugCache } from '../pretty-cache';
import { chapterUrlSuffix } from '../chapter-volume';
import { prettyWorkPath } from '../pretty-path';

/**
 * Посилання на розділ ІЗ ЗБЕРЕЖЕНОЮ ПОЗИЦІЄЮ ЧИТАННЯ. Це критично: без
 * progress/page reader завжди відкриває розділ "з нуля" — саме це раніше й
 * ламало відновлення прогресу для записів з "Історії".
 *
 * Для anime — окрема читалка взагалі не використовується (немає окремої
 * сторінки-читалки для серій, плеєр вбудований у сторінку твору), тож
 * ведемо прямо на неї: вона сама, побачивши, що твір уже в бібліотеці зі
 * збереженим animeSelection, одразу відкриє потрібний переклад і плеєр.
 */
export function readerUrl(h: HistoryItem, contentTypes: Record<string, 'novel' | 'manga' | 'anime'>): string {
  if (contentTypes[h.pluginId] === 'anime') {
    return workHref(h.pluginId, h.novelPath, 'anime', '&from=history');
  }
  const extra = new URLSearchParams({ from: 'history' });
  if (h.page) {
    // manga-джерело: позиція зберігається як номер сторінки, а не %.
    extra.set('page', String(h.page));
  } else if (h.progress > 0 && h.progress < 100) {
    extra.set('progress', String(h.progress));
  }

  const cachedSlug = getCachedSlug(h.pluginId, h.novelPath);
  if (cachedSlug && h.chapterNumber !== undefined) {
    return `${prettyWorkPath(h.pluginId, cachedSlug, chapterUrlSuffix(h.chapterNumber))}?${extra.toString()}`;
  }

  const params = new URLSearchParams({
    plugin: h.pluginId,
    novelPath: h.novelPath,
    novelName: h.novelName,
    path: h.chapterPath,
    name: h.chapterName,
  });
  if (h.chapterNumber !== undefined) params.set('num', String(h.chapterNumber));
  if (h.cover) params.set('cover', h.cover);
  for (const [k, v] of extra) params.set(k, v);
  return `/reader?${params.toString()}`;
}

/**
 * ЗБЕРЕЖЕНА СТАРА ВЕРСІЯ сторінки «Історія» (до редизайну v2) — точка відкату: див. history.ts
 * (перемикач HISTORY_V2_ENABLED) і docs/history-v2.md. Не змінювати.
 */
export async function initHistoryLegacy() {
  if (!(await requireAuthOrRedirect())) return;
  renderNav('history');
  const contentTypes = await pluginContentTypes();

  const list = document.getElementById('list') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;
  const titleRow = document.getElementById('history-title-row') as HTMLElement;
  const searchBar = document.getElementById('history-searchbar') as HTMLElement;
  const searchToggle = document.getElementById('history-search-toggle') as HTMLButtonElement;
  const searchClose = document.getElementById('history-search-close') as HTMLButtonElement;
  const searchInput = document.getElementById('search') as HTMLInputElement;
  const clearBtn = document.getElementById('history-clear-btn') as HTMLButtonElement;

  let items: HistoryItem[] = [];

  async function load() {
    try {
      items = await api.history();
    } catch (err) {
      toast((err as Error).message);
      items = [];
    }
    render(currentFiltered());
    warmSlugCache(
      items.map((h) => ({ pluginId: h.pluginId, path: h.novelPath, title: h.novelName })),
      () => render(currentFiltered()),
    );
  }

  function currentFiltered(): HistoryItem[] {
    const q = searchInput.value.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.novelName.toLowerCase().includes(q));
  }

  function render(entries: HistoryItem[]) {
    list.innerHTML = '';
    if (!entries.length) {
      empty.style.display = 'flex';
      list.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    list.style.display = 'block';

    let currentGroup = '';
    for (const h of entries) {
      const group = fmtDateGroup(h.updatedAt);
      if (group !== currentGroup) {
        currentGroup = group;
        const label = document.createElement('div');
        label.className = 'section-label';
        label.textContent = group;
        list.appendChild(label);
      }

      const a = document.createElement('a');
      a.className = 'row';
      a.href = readerUrl(h, contentTypes);

      // обкладинка твору (не сторінки розділу) — клас cardcover лишаємо,
      // щоб працювали плавна поява зображення та коректний object-fit.
      const cover = coverEl(h.novelName, h.cover, h.novelId);
      cover.classList.add('cover');

      const meta = document.createElement('div');
      meta.className = 'meta';
      const title = document.createElement('p');
      title.className = 'title';
      title.textContent = h.novelName;
      const sub = document.createElement('p');
      sub.className = 'sub';
      sub.textContent = `${h.chapterName} • ${fmtTime(h.updatedAt)}`;
      const progressText = h.totalPages ? `Стор. ${h.page}/${h.totalPages}` : `Прочитано ${h.progress}%`;
      const progressRow = document.createElement('div');
      progressRow.className = 'history-progress';
      progressRow.innerHTML = `<span class="history-progress-track"><span class="history-progress-fill" style="width:${h.progress}%"></span></span><span class="history-progress-pct">${progressText}</span>`;
      meta.appendChild(title);
      meta.appendChild(sub);
      meta.appendChild(progressRow);

      const delBtn = document.createElement('button');
      delBtn.className = 'iconbtn trailing-icon';
      delBtn.setAttribute('aria-label', 'Видалити з історії');
      delBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"/><path d="M10 11v6M14 11v6"/></svg>';
      delBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeItem(h);
      });

      a.appendChild(cover);
      a.appendChild(meta);
      a.appendChild(delBtn);
      list.appendChild(a);
    }
  }

  async function removeItem(h: HistoryItem) {
    try {
      await api.removeHistoryItem(h.novelId);
    } catch (err) {
      toast((err as Error).message);
      return;
    }
    items = items.filter((i) => i.novelId !== h.novelId);
    render(currentFiltered());
  }

  function openSearch() {
    titleRow.style.display = 'none';
    searchBar.style.display = 'flex';
    searchInput.focus();
  }

  function closeSearch() {
    searchInput.value = '';
    searchBar.style.display = 'none';
    titleRow.style.display = 'flex';
    render(currentFiltered());
  }

  searchToggle.addEventListener('click', openSearch);
  searchClose.addEventListener('click', closeSearch);
  searchInput.addEventListener('input', () => render(currentFiltered()));

  clearBtn.addEventListener('click', async () => {
    if (!items.length) return;
    const ok = await confirmDialog({
      title: 'Очистити історію читання?',
      message: 'Список переглянутого буде видалено. Статус прочитаних розділів не зміниться.',
      confirmText: 'Очистити',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.clearHistory();
    } catch (err) {
      toast((err as Error).message);
      return;
    }
    items = [];
    render([]);
    toast('Історію очищено');
  });

  await load();
}
